import React from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import GolfIcon from './GolfIcon';
import { Minimize, Maximize, Volume2, VolumeOff } from 'lucide-react';

interface VideoJSProps {
  options: {
    autoplay: boolean;
    controls: boolean;
    preload: string;
    fluid: boolean;
    fill?: boolean;
    poster?: string;
    playsinline?: boolean;
    loop: boolean;
    responsive: boolean;
    sources: { src: string; type: string }[];
  };
  onReady?: (player: videojs.Player) => void;
  className?: string;
  displayVolumeButton?: boolean;
  displayFullScreen?: boolean;
}

const VideoJS = ({ options, onReady, className, displayVolumeButton = false, displayFullScreen = true }: VideoJSProps) => {
  const videoRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<videojs.Player | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const toggleMute = () => {
    if (playerRef.current) {
      playerRef.current.muted(!playerRef.current.muted());
    }
  };

  const toggleFullscreen = () => {
    if (playerRef.current) {
      if (playerRef.current.isFullscreen()) {
        playerRef.current.exitFullscreen();
      } else {
        playerRef.current.requestFullscreen();
      }
    }
  };

  React.useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-golf-theme');
      videoRef.current.appendChild(videoElement);

      const player = playerRef.current = videojs(videoElement, {
        ...options,
        controlBar: {
          children: [
            'progressControl',
          ],
        },
        userActions: {
          hotkeys: false,
          doubleClick: false,
        },
        inactivityTimeout: 2000,
      }, () => {
        onReady?.(player);

        // Sync mute state with player
        setIsMuted(player.muted());

        // Listen for mute changes
        player.on('volumechange', () => {
          setIsMuted(player.muted());
        });

        // Listen for fullscreen changes
        player.on('fullscreenchange', () => {
          setIsFullscreen(player.isFullscreen());
        });
      });

      player.on('play', () => setIsPlaying(true));
      player.on('pause', () => setIsPlaying(false));
      player.on('ended', () => setIsPlaying(false));

      player.on('click', () => {
        if (player.paused()) {
          player.play();
        } else {
          player.pause();
        }
      });

      // Start muted (like social apps)
      player.muted(true);
    }

    return () => {
      if (playerRef.current) {
        if (!playerRef.current.isDisposed()) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
      }
    };
  }, [options, videoRef]);

  return (
    <div
      data-vjs-player
      className={`relative bg-black ${className} rounded-md`}
    >
      <div ref={videoRef} className="size-full rounded-md" />

      {/* Custom play/pause overlay button */}
      {!isPlaying && (
        <button
          className="absolute inset-0 w-full h-full flex items-center justify-center z-40"
          onClick={() => playerRef.current?.play()}
        >
          <GolfIcon name="play" className="size-40 opacity-80 invert" />
        </button>
      )}
      {displayFullScreen && (
        <button
          className="absolute top-4 right-4 size-10 flex items-center justify-center bg-black bg-opacity-30 rounded-full z-50"
          onClick={toggleFullscreen}
        >
          {isFullscreen ? (
            <Minimize name="fullscreen-exit" className="size-5 opacity-80" />
          ) : (
            <Maximize name="fullscreen" className="size-5 opacity-80" />
          )}
        </button>
      )}
      {displayVolumeButton && (
        <button
          className="absolute bottom-4 right-4 size-10 flex items-center justify-center text-white bg-black bg-opacity-30 rounded-full z-50"
          onClick={toggleMute}
        >
          {isMuted ? (
            <VolumeOff name="volume-mute" className="size-5 hover:opacity-100 opacity-80" />
          ) : (
            <Volume2 name="volume" className="size-5 hover:opacity-100 opacity-80" />
          )}
        </button>
      )
      }
    </div>
  );
};

export default VideoJS;